import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { ThemeProvider } from "./context/ThemeContext";
import Login from "./pages/Login";
import Dashboard from "./pages/Dashboard";
import UnitSelector from "./pages/UnitSelector";
const Masters = lazy(() => import("./pages/Masters"));
const MachineGroups = lazy(() => import("./pages/MachineGroups"));
const SpareParts = lazy(() => import("./pages/SpareParts"));
const MonthlyProductionSheet = lazy(() => import("./pages/MonthlyProductionSheet"));
const ScheduleSetup = lazy(() => import("./pages/ScheduleSetup"));
const ToolHistory = lazy(() => import("./pages/ToolHistory"));
const Maintenance = lazy(() => import("./pages/Maintenance"));
const Alerts = lazy(() => import("./pages/Alerts"));
const Reports = lazy(() => import("./pages/Reports"));
const ToolLifeMaster = lazy(() => import("./pages/ToolLifeMaster"));
const UserManagement = lazy(() => import("./pages/UserManagement"));
const ApiKeys = lazy(() => import("./pages/ApiKeys"));
const HrKpiExport = lazy(() => import("./pages/HrKpiExport"));
const PredictiveAnalytics = lazy(() => import("./pages/PredictiveAnalytics"));
const CapacityPlanning = lazy(() => import("./pages/CapacityPlanning"));
const ManpowerRequirement = lazy(() => import("./pages/ManpowerRequirement"));
const RBACSettings = lazy(() => import("./pages/RBACSettings"));
const AuditLog = lazy(() => import("./pages/AuditLog"));
const ToolScan = lazy(() => import("./pages/ToolScan"));
const BackupRestore = lazy(() => import("./pages/BackupRestore"));
const Planning = lazy(() => import("./pages/Planning"));
const JobWork = lazy(() => import("./pages/JobWork"));
const StoreGodown = lazy(() => import("./pages/StoreGodown"));
const BopReceiving = lazy(() => import("./pages/BopReceiving"));
const Inventory = lazy(() => import("./pages/Inventory"));
const Traceability = lazy(() => import("./pages/Traceability"));
const TraceScan = lazy(() => import("./pages/TraceScan"));
const RawMaterial = lazy(() => import("./pages/RawMaterial"));
const RedTagCost = lazy(() => import("./pages/RedTagCost"));
const PreviousData = lazy(() => import("./pages/PreviousData"));
const Rework = lazy(() => import("./pages/Rework"));
const PressShopEntry = lazy(() => import("./pages/PressShopEntry"));
const MachineShopEntry = lazy(() => import("./pages/MachineShopEntry"));
const MachineProductionSheet = lazy(() => import("./pages/MachineProductionSheet"));
const FinalInspectionEntry = lazy(() => import("./pages/FinalInspectionEntry"));
const COPQ = lazy(() => import("./pages/COPQ"));
const PurchaseOrders = lazy(() => import("./pages/PurchaseOrders"));
import Layout from "./components/Layout";
import "./App.css";

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }
  
  if (!user) {
    // Remember where they were headed (e.g. a scanned /trace/{token}) so login
    // can return them there instead of dumping them on the dashboard. Internal
    // paths only — guards against open-redirect.
    const dest = location.pathname + location.search;
    const from = dest.startsWith("/") && !dest.startsWith("//") && dest !== "/login" ? dest : null;
    return <Navigate to="/login" replace state={from ? { from } : undefined} />;
  }

  return children;
};

// Route-level view-permission guard. Mirrors the sidebar filter in Layout.js exactly:
// backup → role "management" only; admin/management bypass for quality_* modules;
// everything else → hasPermission(module, "view"). ProtectedRoute already guarantees
// `user` is loaded before this renders, so there is no loading race.
const RequireModule = ({ module, children }) => {
  const { user, hasPermission } = useAuth();
  const allowed =
    module === "backup"
      ? user?.role === "management"
      : (user?.role === "admin" || user?.role === "management")
      ? true
      : hasPermission(module, "view");
  if (!allowed) {
    return <Navigate to="/dashboard" replace />;
  }
  return children;
};

// Route guard passing if the user has ANY of the listed modules' view permission.
// Same admin/management bypass as RequireModule.
const RequireAnyModule = ({ modules, children }) => {
  const { user, hasPermission } = useAuth();
  const allowed =
    (user?.role === "admin" || user?.role === "management")
      ? true
      : modules.some((m) => hasPermission(m, "view"));
  if (!allowed) {
    return <Navigate to="/dashboard" replace />;
  }
  return children;
};

const PageLoader = () => (
  <div className="min-h-[50vh] flex items-center justify-center">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
  </div>
);

function AppRoutes() {
  return (
    <Suspense fallback={<PageLoader />}>
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/tool-scan/:operationId" element={<ToolScan />} />
      <Route path="/select-unit" element={
        <ProtectedRoute>
          <UnitSelector />
        </ProtectedRoute>
      } />
      <Route path="/" element={
        <ProtectedRoute>
          <Layout />
        </ProtectedRoute>
      }>
        <Route index element={<Dashboard />} />
        <Route path="dashboard" element={<Dashboard />} />
        <Route path="production-sheet" element={<RequireModule module="production_sheet"><MonthlyProductionSheet /></RequireModule>} />
        <Route path="machine-production-sheet" element={<RequireModule module="machine_production_sheet"><MachineProductionSheet /></RequireModule>} />
        <Route path="assembly-production-sheet" element={<RequireModule module="machine_production_sheet"><MachineProductionSheet mode="assembly" /></RequireModule>} />
        <Route path="schedule-setup" element={<RequireModule module="schedule_setup"><ScheduleSetup /></RequireModule>} />
        <Route path="purchase" element={<RequireModule module="purchase"><PurchaseOrders /></RequireModule>} />
        <Route path="tool-history" element={<RequireModule module="tool_history"><ToolHistory /></RequireModule>} />
        <Route path="spare-parts" element={<RequireModule module="maintenance"><SpareParts /></RequireModule>} />
        <Route path="maintenance" element={<RequireModule module="maintenance"><Maintenance /></RequireModule>} />
        <Route path="alerts" element={<RequireModule module="alerts"><Alerts /></RequireModule>} />
        <Route path="reports" element={<RequireAnyModule modules={["reports_production", "reports_quality", "reports_jobwork", "reports_fg", "reports_machines", "bop"]}><Reports /></RequireAnyModule>} />
        <Route path="tool-life-master" element={<RequireModule module="tool_life_master"><ToolLifeMaster /></RequireModule>} />
        <Route path="user-management" element={<RequireModule module="user_management"><UserManagement /></RequireModule>} />
        <Route path="api-keys" element={<RequireModule module="user_management"><ApiKeys /></RequireModule>} />
        <Route path="hr-kpi-export" element={<RequireModule module="user_management"><HrKpiExport /></RequireModule>} />
        <Route path="predictive-analytics" element={<RequireModule module="analytics"><PredictiveAnalytics /></RequireModule>} />
        <Route path="capacity-planning" element={<RequireModule module="capacity_planning"><CapacityPlanning /></RequireModule>} />
        <Route path="manpower-requirement" element={<RequireModule module="capacity_planning"><ManpowerRequirement /></RequireModule>} />
        <Route path="planning" element={<RequireModule module="planning"><Planning /></RequireModule>} />
        <Route path="jobwork" element={<RequireModule module="jobwork"><JobWork /></RequireModule>} />
        <Route path="store-godown" element={<RequireModule module="store"><StoreGodown /></RequireModule>} />
        <Route path="bop-receiving" element={<RequireModule module="bop"><BopReceiving /></RequireModule>} />
        <Route path="inventory" element={<RequireModule module="inventory"><Inventory /></RequireModule>} />
        <Route path="traceability" element={<RequireAnyModule modules={["production_sheet", "rework"]}><Traceability /></RequireAnyModule>} />
        <Route path="trace/:id" element={<RequireAnyModule modules={["production_sheet", "rework"]}><TraceScan /></RequireAnyModule>} />
        <Route path="raw-material" element={<RequireModule module="raw_material"><RawMaterial /></RequireModule>} />
        <Route path="rbac-settings" element={<RequireModule module="rbac_settings"><RBACSettings /></RequireModule>} />
        <Route path="audit-log" element={<RequireModule module="audit_logs"><AuditLog /></RequireModule>} />
        <Route path="masters" element={<RequireModule module="masters"><Masters /></RequireModule>} />
        <Route path="machine-groups" element={<RequireModule module="masters"><MachineGroups /></RequireModule>} />
        <Route path="quality/red-tag-cost" element={<RequireModule module="quality_red_tag"><RedTagCost /></RequireModule>} />
        <Route path="previous-data" element={<RequireModule module="reports_production"><PreviousData /></RequireModule>} />
        <Route path="rework" element={<RequireModule module="rework"><Rework /></RequireModule>} />
        <Route path="quality/press-shop" element={<RequireModule module="quality_press_shop"><PressShopEntry /></RequireModule>} />
        <Route path="quality/machine-shop" element={<RequireModule module="quality_machine_shop"><MachineShopEntry /></RequireModule>} />
        <Route path="quality/final-inspection" element={<RequireModule module="quality_final_inspection"><FinalInspectionEntry /></RequireModule>} />
        <Route path="quality/copq" element={<RequireModule module="quality_copq"><COPQ /></RequireModule>} />
        <Route path="backup" element={<RequireModule module="backup"><BackupRestore /></RequireModule>} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </Suspense>
  );
}

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <BrowserRouter>
          <AppRoutes />
          <Toaster position="top-right" richColors />
        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
